/**
 * D-EZ-104 — THE STAKES CARD. The TV graphic that comes up before a pivotal
 * throw: when it is, who is in it, the one number that matters, what each die
 * needs, and what rides on it. Then the commissioner throws.
 *
 * Presentation only. Every word on it comes from `presentation/stakes/stakes.ts`,
 * which builds the card from the board as it stood before the throw and the
 * printed rule — never from the throw it introduces.
 */

import { useEffect, useRef } from 'react';
import type { StakesCard as Card } from '../presentation/stakes/stakes.ts';
import { Cite } from './Cite.tsx';

/** Space or Enter throws the dice — the two keys a person reaches for. */
export function isRollKey(event: { key: string; code?: string }): boolean {
  return event.key === ' ' || event.key === 'Enter' || event.code === 'Space' || event.key === 'Spacebar';
}

const KIND_WORDS: Record<Card['kind'], string> = {
  'lead-challenge': 'For the lead',
  'top-challenge': 'Top seven',
  'pit-lead': 'Off pit road',
  trouble: 'Trouble',
  rare: 'Rare result',
  rain: 'Rain',
  'photo-finish': 'At the line',
};

export interface StakesCardProps {
  card: Card;
  /** Which of the race's commissioner throws this is (1-based), for the eyebrow. */
  number?: number;
  /** 'ready': waiting for the throw · 'rolling': the dice are down · 'result': the line is in. */
  stage: 'ready' | 'rolling' | 'result';
  /** The one bold line after the throw. */
  result?: string;
  onRoll?: () => void;
  /** The Roll button's words ("Roll", "Roll the white die", "Roll red and blue"). */
  rollLabel?: string;
  /** Move on after the result (also Space/Enter). */
  onContinue?: () => void;
}

export default function StakesCard({ card, number, stage, result, onRoll, rollLabel = 'Roll', onContinue }: StakesCardProps) {
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const b = button.current;
    if (!b || (stage !== 'ready' && stage !== 'result')) return;
    b.focus({ preventScroll: true });
    // The Roll is the thing to see: bring it into view (on a phone the board sits above the table).
    try {
      b.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch {
      /* no layout (tests) */
    }
  }, [stage, card.title]);
  const id = `stakes-${card.kind}`;
  return (
    <section className={`stakes stakes--${card.kind} stakes--${stage}`} aria-labelledby={`${id}-title`} aria-describedby={`${id}-head`}>
      <header className="stakes__top">
        <p className="stakes__when">{card.when}</p>
        <p className="stakes__kind">{KIND_WORDS[card.kind]}{number ? ` · throw ${number}` : ''}</p>
      </header>
      <h3 className="stakes__title" id={`${id}-title`}>{card.title}</h3>
      {card.headline && (
        <p className="stakes__headline" id={`${id}-head`}>
          <b className={`stakes__odds${/%/.test(card.headline.odds ?? '') ? '' : ' stakes__odds--word'}`}>{card.headline.odds}</b>
          <span>{card.headline.text}</span>
        </p>
      )}
      {stage !== 'result' && card.needs.length > 0 && (
        <ul className="stakes__needs" aria-label="What the dice need">
          {card.needs.slice(0, 4).map((n) => (
            <li key={n.text} className={`stakes__need${n.odds ? "" : " stakes__need--note"}`}>
              <span>{n.text}</span>
              {n.odds && <b className="stakes__pill">{n.odds}</b>}
            </li>
          ))}
        </ul>
      )}
      {stage !== 'result' && card.rides.length > 0 && (
        <ul className="stakes__rides" aria-label="What rides on it">
          {card.rides.slice(0, 2).map((r) => (
            <li key={r} className="stakes__ride">{r}</li>
          ))}
        </ul>
      )}
      <Cite className="stakes__cite">{card.cite}</Cite>
      {stage === 'ready' && onRoll && (
        <button
          ref={button}
          type="button"
          className="stakes__roll"
          onClick={onRoll}
          aria-keyshortcuts="Space Enter"
          aria-label={`${rollLabel}: ${card.title}`}
        >
          {rollLabel} <small className="stakes__key">Space · Enter</small>
        </button>
      )}
      {stage === 'rolling' && <p className="stakes__wait" role="status">The dice are down…</p>}
      {stage === 'result' && (
        <div className="stakes__result" role="status" aria-live="assertive">
          <strong className="stakes__line">{result}</strong>
          {onContinue && (
            <button ref={button} type="button" className="stakes__next" onClick={onContinue} aria-keyshortcuts="Space Enter">
              Race on
            </button>
          )}
        </div>
      )}
    </section>
  );
}
